/** A schema by name and version. */
export interface SchemaRef {
  schema: string;
  version: string;
}

interface OwnerCandidate {
  direction?: unknown;
  ownerResource?: unknown;
  ownerVersion?: unknown;
  constraints?: { cardinality?: unknown; cascadeOnRemove?: unknown };
  metadata?: { constraints?: { cardinality?: unknown; cascadeOnRemove?: unknown } };
}

// Removing the owner removes (or deactivates) the owned records — a composition, not a reference.
const OWNING_CASCADES = new Set(["DELETE_SOURCE_AND_TARGETS", "DEACTIVATE_SOURCE_AND_TARGETS"]);

const relationshipMapOf = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  const root = payload as Record<string, unknown>;
  const nested = root.data;
  const hasArrays = Object.values(root).some((value) => Array.isArray(value));

  if (!hasArrays && nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }

  return root;
};

const ownerOf = (item: OwnerCandidate, schema: string): SchemaRef | undefined => {
  const constraints = item.constraints ?? item.metadata?.constraints;

  if (item.direction !== "incoming" || typeof item.ownerResource !== "string") {
    return undefined;
  }

  const cardinality = constraints?.cardinality;
  if (
    item.ownerResource === schema ||
    (cardinality !== "ONE_TO_MANY" && cardinality !== "ONE_TO_ONE")
  ) {
    return undefined;
  }

  const cascadeOnRemove = constraints?.cascadeOnRemove;
  if (typeof cascadeOnRemove !== "string" || !OWNING_CASCADES.has(cascadeOnRemove)) {
    return undefined;
  }

  return {
    schema: item.ownerResource,
    version: typeof item.ownerVersion === "string" ? item.ownerVersion : "1.0.0",
  };
};

/**
 * The schema whose records own `schema`'s records, read from the schema registry's relationship
 * payload for `schema` (`GET /schema/{schema}/{version}/relationships`): an incoming ONE_TO_MANY
 * relationship from another schema whose removal cascades to its targets. `ColorInPalette` →
 * `Palette`. `undefined` when the schema stands on its own.
 */
export const ownerSchemaOf = (payload: unknown, schema: string): SchemaRef | undefined =>
  ownerSchemasOf(payload, schema)[0];

/** Every schema that owns `schema`'s records, in payload order. See {@link ownerSchemaOf}. */
export const ownerSchemasOf = (payload: unknown, schema: string): SchemaRef[] => {
  const owners: SchemaRef[] = [];
  for (const value of Object.values(relationshipMapOf(payload))) {
    if (!Array.isArray(value)) {
      continue;
    }

    for (const item of value) {
      const owner =
        item && typeof item === "object" ? ownerOf(item as OwnerCandidate, schema) : undefined;

      if (owner) {
        owners.push(owner);
      }
    }
  }

  return owners;
};

const SCHEMA_ROUTE = /^\/([A-Z][A-Za-z0-9]*)\/(\d+\.\d+\.\d+)(?:\/|$)/;

/**
 * The schema a generic record route shows — `/ColorInPalette/1.0.0` or
 * `/ColorInPalette/1.0.0/<pk>`. App routes are lower-case (`/colours-palettes`), schema names are
 * PascalCase, so the two never collide.
 */
export const schemaOfRoute = (pathname: string): SchemaRef | undefined => {
  const match = SCHEMA_ROUTE.exec(pathname);

  return match ? { schema: match[1], version: match[2] } : undefined;
};
